import React from 'react';
import PropTypes from 'prop-types';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    InputBase,
    List,
    ListItemButton,
    ListItemAvatar,
    Avatar,
    ListItemText,
    Radio,
    Typography,
    Divider,
} from '@mui/material';
import { Icon } from '@iconify/react';
import { useSelector, shallowEqual } from 'react-redux';
import { piiProps } from 'src/components/DemoBlur';
import { COLORS, getAvatarColor } from './styles';
import { fetchContactDetails, stringAvatar } from './whatsappUtil';

// ForwardPickerDialog — single-target forward picker per D11.
//
// The thread list is derived from the loaded `threadMessages` slice (same
// source `ConversationSearchModal` uses) so we don't need a network round
// trip. A simple substring match handles the search field.
//
// One radio-select per session; "Forward" is disabled until a target is
// chosen. Caller passes `count` so the header reads "{count} message{s} to forward".
const ForwardPickerDialog = ({ open, count, currentThreadId, onClose, onConfirm }) => {
    const [query, setQuery] = React.useState('');
    const [targetId, setTargetId] = React.useState(null);

    const { threadMessages } = useSelector(
        (state) => ({ threadMessages: state?.whatsappV2?.threadMessages || [] }),
        shallowEqual,
    );

    React.useEffect(() => {
        if (!open) {
            setQuery('');
            setTargetId(null);
        }
    }, [open]);

    const entries = React.useMemo(() => (
        (threadMessages || []).map((thread) => {
            const details = fetchContactDetails(thread) || {};
            const { name = '', phoneNumber = '', avatar, preview = '' } = details;
            return {
                id: thread?.id,
                thread,
                name,
                phoneNumber,
                avatar,
                preview,
                nameLower: (name || '').toLowerCase(),
                phoneLower: (phoneNumber || '').toLowerCase(),
                isCurrent: thread?.id != null && currentThreadId != null && `${thread.id}` === `${currentThreadId}`,
            };
        })
    ), [threadMessages, currentThreadId]);

    const filtered = React.useMemo(() => {
        const q = (query || '').trim().toLowerCase();
        if (!q) return entries;
        return entries.filter(e => e.nameLower.includes(q) || e.phoneLower.includes(q));
    }, [entries, query]);

    const handleConfirm = () => {
        if (targetId == null) return;
        onConfirm(targetId);
    };

    const headerText = `${count} message${count === 1 ? '' : 's'} to forward`;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="sm"
            aria-labelledby="forward-picker-title"
            PaperProps={{ sx: { borderRadius: 2, height: '70vh', display: 'flex', flexDirection: 'column' } }}
        >
            <DialogTitle
                id="forward-picker-title"
                sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: 16 }}
            >
                <Icon icon="mdi:share-outline" width={22} height={22} style={{ color: COLORS.primary }} />
                Forward to…
                <Typography sx={{ ml: 'auto', fontSize: 12, color: COLORS.textSecondary }}>
                    {headerText}
                </Typography>
            </DialogTitle>
            <Divider />
            <Box sx={{ px: 2.5, py: 1.5 }}>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        px: 1.5,
                        py: 0.75,
                        borderRadius: 1.5,
                        backgroundColor: COLORS.backgroundLight,
                        border: `1px solid ${COLORS.dividerLight}`,
                    }}
                >
                    <Icon icon="mdi:magnify" width={18} height={18} style={{ color: COLORS.textSecondary }} />
                    <InputBase
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Search contacts"
                        sx={{ flex: 1, fontSize: 14 }}
                        inputProps={{ 'aria-label': 'Search contacts to forward to' }}
                    />
                </Box>
            </Box>
            <DialogContent dividers sx={{ p: 0, flex: 1, overflowY: 'auto' }}>
                {filtered.length === 0 ? (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            py: 6,
                            px: 3,
                            color: COLORS.textSecondary,
                            textAlign: 'center',
                        }}
                    >
                        <Icon icon="mdi:account-search-outline" width={36} height={36} />
                        <Typography sx={{ mt: 1, fontSize: 13 }}>
                            {query ? 'No contacts match your search.' : 'No contacts available.'}
                        </Typography>
                    </Box>
                ) : (
                    <List disablePadding>
                        {filtered.map((entry) => (
                            <ListItemButton
                                key={entry.id}
                                onClick={() => setTargetId(entry.id)}
                                disabled={entry.isCurrent}
                                selected={targetId === entry.id}
                                sx={{
                                    px: 2,
                                    py: 1,
                                    borderBottom: `1px solid ${COLORS.dividerLighter}`,
                                    '&.Mui-selected': { backgroundColor: COLORS.primaryLighter },
                                }}
                            >
                                <ListItemAvatar {...piiProps}>
                                    {entry.avatar ? (
                                        <Avatar src={entry.avatar} alt={entry.name || entry.phoneNumber} />
                                    ) : (
                                        <Avatar sx={{ bgcolor: getAvatarColor(entry.name || entry.phoneNumber) }}>
                                            {stringAvatar(entry.name || entry.phoneNumber)}
                                        </Avatar>
                                    )}
                                </ListItemAvatar>
                                <ListItemText
                                    primary={entry.name || entry.phoneNumber}
                                    {...piiProps}
                                    secondary={entry.isCurrent ? 'Current chat' : (entry.preview || entry.phoneNumber)}
                                    primaryTypographyProps={{ fontSize: 14, fontWeight: 500 }}
                                    secondaryTypographyProps={{
                                        fontSize: 12,
                                        color: COLORS.textSecondary,
                                        sx: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
                                    }}
                                />
                                <Radio
                                    checked={targetId === entry.id}
                                    edge="end"
                                    aria-label={`Forward to ${entry.name || entry.phoneNumber}`}
                                />
                            </ListItemButton>
                        ))}
                    </List>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 2.5, py: 1.5 }}>
                <Button onClick={onClose} color="inherit">Cancel</Button>
                <Button
                    onClick={handleConfirm}
                    variant="contained"
                    color="primary"
                    disabled={targetId == null}
                    startIcon={<Icon icon="mdi:share-outline" width={16} height={16} />}
                >
                    Forward
                </Button>
            </DialogActions>
        </Dialog>
    );
};

ForwardPickerDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    count: PropTypes.number.isRequired,
    currentThreadId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    onClose: PropTypes.func.isRequired,
    onConfirm: PropTypes.func.isRequired,
};

export default ForwardPickerDialog;
